/*
 * MarineMaker order form helpers (Enterprise 2.3.0, product.order-* templates)
 *
 * Why this exists: Custom Liquid block settings can't read `section`, so the HTML in those blocks
 * can't know the product form id. Every such input carries `data-mm-prop`; we attach it to the
 * product form here so it submits with Add to cart as a line-item property (name="properties[...]").
 *
 * Also: file-upload feedback, boat-name previews in the style cards, size-tier suggestion
 * (auto-selects the matching Size variant), registration preview, scroll-to-first-invalid.
 * No dependencies. Safe to load on every page (it exits when there is no main product form).
 */
(() => {
  const MAX_UPLOAD_MB = 20; // [CONFIRM] keep at or below Shopify's limit for line-item property uploads

  const byProp = (label) => document.querySelector(`[name="properties[${CSS.escape(label)}]"]`);
  const num = (el) => (el ? parseFloat(String(el.value).replace(',', '.')) : NaN);

  // Size tiers: values must match the START of the product's "Size" option values, e.g. "Small (up to 24 in)".
  const TIERS = {
    width: [['Small', 24], ['Medium', 48], ['Large', 72], ['Extra large', 96]],
    max: [['Small', 6], ['Medium', 12], ['Large', 24]]
  };

  function bindToForm(form) {
    document.querySelectorAll('[data-mm-prop]').forEach((el) => el.setAttribute('form', form.id));
  }

  function uploads() {
    document.querySelectorAll('[data-mm-upload]').forEach((input) => {
      const drop = input.closest('.mm-upload__drop');
      const out = drop && drop.querySelector('.mm-upload__file');
      input.addEventListener('change', () => {
        const f = input.files && input.files[0];
        drop.classList.remove('is-error', 'has-file');
        drop.style.removeProperty('--mm-preview');
        if (!f) { out.textContent = ''; return; }
        if (f.size > MAX_UPLOAD_MB * 1024 * 1024) {
          input.value = '';
          out.textContent = `That file is over ${MAX_UPLOAD_MB} MB. Please choose a smaller one.`;
          drop.classList.add('is-error');
          return;
        }
        out.textContent = `✓ ${f.name}`;
        drop.classList.add('has-file');
        if (/^image\/(jpe?g|png|webp|gif)$/i.test(f.type)) {
          drop.style.setProperty('--mm-preview', `url("${URL.createObjectURL(f)}")`);
        }
      });
    });
  }

  function namePreview() {
    const name = byProp('Boat name (exactly as it should appear)');
    if (!name) return;
    const targets = document.querySelectorAll('[data-mm-name-preview]:not(.mm-font-ui)');
    const update = () => targets.forEach((t) => { t.textContent = name.value.trim() || 'Halcyon'; });
    name.addEventListener('input', update);
    update();
  }

  function sizeHelper() {
    const hint = document.querySelector('[data-mm-size-helper]');
    if (!hint) return;
    const by = hint.dataset.by;
    const w = byProp(hint.dataset.width);
    const h = hint.dataset.height ? byProp(hint.dataset.height) : null;
    const sizeInputs = () => Array.from(document.querySelectorAll('input.js-option')).filter((i) => TIERS[by].some(([t]) => i.value.startsWith(t)));
    const run = () => {
      const n = by === 'width' ? num(w) : Math.max(num(w) || 0, num(h) || 0);
      if (!(n > 0)) { hint.textContent = 'Enter your measurements and we’ll suggest a size.'; return; }
      const tier = TIERS[by].find(([, max]) => n <= max);
      if (!tier) { hint.textContent = 'That’s larger than our biggest size. Please contact us for a quote before ordering.'; hint.classList.add('is-warn'); return; }
      hint.classList.remove('is-warn');
      const match = sizeInputs().filter((i) => i.value.startsWith(tier[0]) && !(tier[0] === 'Large' && i.value.startsWith('Extra')));
      const target = match.find((i) => !i.checked) || match[0];
      if (target && !target.checked) target.click(); // lets the theme update variant, price and URL
      hint.textContent = `Based on ${n} in, ${tier[0]} fits. We’ll confirm the final size on your proof.`;
    };
    [w, h].forEach((el) => el && el.addEventListener('input', run));
    run();
  }

  // Emblem sizing: "Manufacturer sizing" selects its own Size variant and needs no measurements;
  // "Custom" enables width + height (disabled fields neither validate nor submit) and the size helper
  // picks Small/Medium/Large. The Size buttons are shown or hidden by CSS from body[data-mm-sizing],
  // because the theme re-renders the variant picker on every variant change.
  function sizing() {
    const box = document.querySelector('[data-mm-sizing]');
    const custom = document.querySelector('[data-mm-sizing-custom]');
    if (!box || !custom) return;
    const MFR = box.dataset.manufacturerValue || 'Manufacturer sizing';
    const fields = Array.from(custom.querySelectorAll('input'));
    const sizeBtn = (test) => Array.from(document.querySelectorAll('input.js-option')).find(test);
    const apply = () => {
      const v = box.querySelector('input:checked')?.value;
      const mode = !v ? 'none' : v === MFR ? 'manufacturer' : 'custom';
      document.body.dataset.mmSizing = mode;
      custom.hidden = mode !== 'custom';
      fields.forEach((f) => { f.disabled = mode !== 'custom'; });
      if (mode === 'manufacturer') {
        const b = sizeBtn((i) => i.value === MFR);
        if (b && !b.checked) b.click();
      } else if (mode === 'custom') {
        if (sizeBtn((i) => i.value === MFR && i.checked)) sizeBtn((i) => i.value.startsWith('Small'))?.click();
        fields[0].dispatchEvent(new Event('input')); // re-run the size helper with any measurements already typed
      }
    };
    box.addEventListener('change', apply);
    apply();
  }

  // Quantity stepper. The field has no name: its value is copied into the product form's own quantity
  // input at submit (capture phase, before the theme's submit handler builds FormData), because the theme
  // re-renders the add-to-cart area, including that input, on every variant change.
  const MAX_QTY = 99;
  const ownQty = () => document.querySelector('[data-mm-qty]');
  const clampQty = (v) => Math.min(MAX_QTY, Math.max(1, parseInt(v, 10) || 1));

  function quantity(form) {
    const input = ownQty();
    if (!input) return;
    document.querySelectorAll('[data-mm-qty-step]').forEach((b) => b.addEventListener('click', () => {
      input.value = clampQty(clampQty(input.value) + Number(b.dataset.mmQtyStep));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }));
    input.addEventListener('change', () => { input.value = clampQty(input.value); });
    document.addEventListener('submit', (e) => {
      if (e.target !== form) return;
      form.querySelectorAll('[name="quantity"]').forEach((q) => { q.value = clampQty(input.value); });
    }, true);
  }

  // Live price: unit price × quantity = total, refreshed on every selection. Prices come from the
  // product's own variants (/products/<handle>.js), so they always match checkout.
  function summary(form) {
    const box = document.querySelector('[data-mm-summary]');
    if (!box) return;
    const q = (sel) => box.querySelector(sel);
    const fmt = new Intl.NumberFormat('en-CA', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD' });
    const money = (cents) => fmt.format(cents / 100);
    let variants = null;
    let current = null;

    const qty = () => {
      const el = ownQty() || form.querySelector('[name="quantity"]') || document.querySelector(`[name="quantity"][form="${CSS.escape(form.id)}"]`);
      return clampQty(el ? el.value : 1);
    };
    const variant = () => {
      if (current) return current;
      const id = Number(form.querySelector('[name="id"]')?.value);
      return variants ? variants.find((v) => v.id === id) : null;
    };
    const pending = () => {
      const warn = document.querySelector('[data-mm-size-helper].is-warn');
      if (warn && warn.offsetParent) return warn.textContent;
      if (box.dataset.gate !== 'sizing') return '';
      const mode = document.body.dataset.mmSizing;
      if (!mode || mode === 'none') return 'Choose Manufacturer sizing or Custom to see your price.';
      if (mode === 'custom' && !(Math.max(num(byProp('Width (in)')) || 0, num(byProp('Height (in)')) || 0) > 0)) return 'Enter your width and height to see your price.';
      return '';
    };

    function update() {
      const v = variant();
      const wait = pending();
      box.classList.toggle('is-pending', !!wait || !v);
      if (wait || !v) {
        q('[data-mm-summary-item]').textContent = wait || 'Loading your price…';
        q('[data-mm-summary-calc]').textContent = '';
        q('[data-mm-summary-total]').textContent = '';
        return;
      }
      const n = qty();
      const unit = n === 1 ? box.dataset.unit : box.dataset.units;
      const custom = box.dataset.gate === 'sizing' && document.body.dataset.mmSizing === 'custom' ? ' · custom size' : '';
      q('[data-mm-summary-item]').textContent = (v.public_title || v.title || '').split(' / ').join(' · ') + custom;
      q('[data-mm-summary-calc]').textContent = `${money(v.price)} per ${box.dataset.unit} × ${n} ${unit}`;
      q('[data-mm-summary-total]').textContent = `Total ${money(v.price * n)}`;
    }

    let frame;
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
    document.addEventListener('on:variant:change', (e) => { if (e.detail && e.detail.variant) current = e.detail.variant; schedule(); });
    document.addEventListener('input', schedule);
    document.addEventListener('change', schedule);
    fetch(`${location.pathname.replace(/\/$/, '')}.js`).then((r) => r.json()).then((p) => { variants = p.variants; schedule(); }).catch(() => {});
    update();
  }

  function regPreview() {
    const box = document.querySelector('[data-mm-reg-preview]');
    if (!box) return;
    const src = byProp(box.dataset.source);
    if (!src) return;
    const text = box.querySelector('.mm-reg-preview__text');
    const count = box.querySelector('.mm-reg-preview__count');
    const update = () => {
      const pos = src.selectionStart;
      src.value = src.value.toUpperCase();
      if (pos != null) src.setSelectionRange(pos, pos);
      const t = src.value.trim();
      text.textContent = t || 'AB 12345';
      count.textContent = t ? `${t.replace(/\s/g, '').length} characters (spaces not counted)` : '';
    };
    src.addEventListener('input', update);
    update();
  }

  function scrollToFirstInvalid(form) {
    let scheduled = false;
    document.addEventListener('invalid', (e) => {
      const el = e.target;
      if (el.form !== form) return;
      el.closest('.product-info__block, .mm-upload, fieldset')?.classList.add('mm-invalid');
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
        scheduled = false;
      });
    }, true);
    document.addEventListener('change', (e) => e.target.closest?.('.mm-invalid')?.classList.remove('mm-invalid'));
    document.addEventListener('input', (e) => e.target.closest?.('.mm-invalid')?.classList.remove('mm-invalid'));
  }

  function init() {
    const form = document.querySelector('form.js-product-form-main');
    if (!form || !document.querySelector('[data-mm-prop]')) return;
    bindToForm(form);
    uploads();
    namePreview();
    sizeHelper();
    sizing();
    quantity(form);
    summary(form);
    regPreview();
    scrollToFirstInvalid(form);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
